import React from "react";
import { LegalLayout, LegalSection } from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 15, 2026" testId="terms-page">
      <LegalSection title="1. Agreement to Terms">
        <p>
          These Terms of Service ("Terms") are a binding agreement between you and Newground AI
          ("Newground," "we," "us"), a company based in the United States. By creating an account or using
          the Newground platform, you agree to these Terms and our{" "}
          <a href="/privacy" className="text-[#1D6AFF] hover:underline">Privacy Policy</a>. If you do not
          agree, do not use the service.
        </p>
      </LegalSection>

      <LegalSection title="2. The Service">
        <p>
          Newground is an AI growth system for real estate agents that provides AI-powered lead generation
          campaigns, ad creative generation, messaging tools, and related features. Feature availability
          depends on your subscription plan (Free preview, Pro, or Elite). We may add, change, or remove
          features as the product evolves.
        </p>
      </LegalSection>

      <LegalSection title="3. Accounts & Eligibility">
        <p>
          You must be at least 18 years old and use the service for business purposes. You are responsible
          for the accuracy of your account information, for maintaining the confidentiality of your
          credentials, and for all activity under your account. Notify us immediately at{" "}
          <a href="mailto:james@newgroundai.com" className="text-[#1D6AFF] hover:underline">james@newgroundai.com</a>{" "}
          if you suspect unauthorized access.
        </p>
      </LegalSection>

      <LegalSection title="4. Subscriptions, Billing & Cancellation">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Paid plans (Pro and Elite) are billed in advance on a monthly or yearly basis through Stripe at the prices shown at checkout.</li>
          <li>Subscriptions renew automatically at the end of each billing period until canceled.</li>
          <li>You can cancel anytime from your dashboard. Cancellation takes effect at the end of the current billing period; you keep access until then.</li>
          <li>Except where required by law, payments are non-refundable, including for partial billing periods or unused features.</li>
          <li>We may change plan pricing with at least 30 days' notice before it applies to your next renewal.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Advertising Spend">
        <p>
          Your Newground subscription does not include advertising budget. All Meta (Facebook/Instagram) ad
          spend is billed directly to you by Meta through your own ad account. Newground never collects or
          manages your ad budget, and we are not responsible for charges incurred on your Meta ad account.
        </p>
      </LegalSection>

      <LegalSection title="6. Messaging & Compliance (Important)">
        <p>
          When you use Newground's SMS and messaging features, you, not Newground, are the sender of those
          communications. You agree to:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Only message leads and contacts who have given appropriate consent to be contacted, as required by the TCPA and other applicable laws;</li>
          <li>Honor opt-out requests (e.g., STOP replies), which the platform processes automatically;</li>
          <li>Not send spam, deceptive, or otherwise unlawful messages;</li>
          <li>Comply with all applicable real estate advertising rules, fair housing laws, and licensing requirements in your jurisdiction.</li>
        </ul>
        <p>You are solely responsible for the content of the messages and ads you send or publish.</p>
      </LegalSection>

      <LegalSection title="7. AI-Generated Content">
        <p>
          Newground uses third-party AI models to generate ad hooks, creatives, and message drafts. AI output
          may be inaccurate or incomplete; you are responsible for reviewing all generated content before
          using or sending it. Subject to these Terms, you own the content you create with the service and
          the AI outputs generated for you. AI outputs are not legal, financial, or professional advice.
        </p>
      </LegalSection>

      <LegalSection title="8. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Use the service for any unlawful, deceptive, or discriminatory purpose;</li>
          <li>Reverse engineer, scrape, or resell the service or its underlying models;</li>
          <li>Upload content that infringes intellectual property rights or contains malware;</li>
          <li>Interfere with the operation or security of the platform;</li>
          <li>Share your account or circumvent plan limits or feature gating.</li>
        </ul>
        <p>We may suspend or terminate accounts that violate these Terms.</p>
      </LegalSection>

      <LegalSection title="9. Your Data">
        <p>
          You retain ownership of the data you upload (including your lead and contact data). You grant us a
          limited license to host and process that data solely to provide the service. Our handling of
          personal data is described in the{" "}
          <a href="/privacy" className="text-[#1D6AFF] hover:underline">Privacy Policy</a>.
        </p>
      </LegalSection>

      <LegalSection title="10. Intellectual Property">
        <p>
          The Newground platform, including its software, design, branding, and content (excluding your
          data and your AI outputs), is owned by Newground AI and protected by intellectual property laws.
          We grant you a limited, non-exclusive, non-transferable license to use the service while your
          account is in good standing.
        </p>
      </LegalSection>

      <LegalSection title="11. Disclaimers">
        <p>
          The service is provided "as is" and "as available." We do not guarantee any particular business
          results, number of leads, ad performance, or revenue. To the fullest extent permitted by law, we
          disclaim all warranties, express or implied, including merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>
      </LegalSection>

      <LegalSection title="12. Limitation of Liability">
        <p>
          To the fullest extent permitted by law, Newground AI will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for lost profits, revenue, or data.
          Our total liability for any claim arising out of the service will not exceed the amount you paid
          us in the 12 months before the claim arose.
        </p>
      </LegalSection>

      <LegalSection title="13. Indemnification">
        <p>
          You agree to indemnify and hold Newground AI harmless from claims arising out of your use of the
          service, your content and messages, your violation of these Terms, or your violation of any law or
          third-party right (including consent and telemarketing laws).
        </p>
      </LegalSection>

      <LegalSection title="14. Termination">
        <p>
          You may stop using the service and delete your account at any time. We may suspend or terminate
          your access for breach of these Terms, unlawful use, or non-payment. Sections that by their nature
          should survive (including 7, 9–13, and 15) survive termination.
        </p>
      </LegalSection>

      <LegalSection title="15. Governing Law & Disputes">
        <p>
          These Terms are governed by the laws of the United States and the state in which Newground AI is
          organized, without regard to conflict-of-law rules. Before filing any claim, you agree to first
          contact us at{" "}
          <a href="mailto:james@newgroundai.com" className="text-[#1D6AFF] hover:underline">james@newgroundai.com</a>{" "}
          so we can try to resolve the dispute informally.
        </p>
      </LegalSection>

      <LegalSection title="16. Changes to These Terms">
        <p>
          We may update these Terms from time to time. If we make material changes, we will notify you by
          email or in-app notice at least 14 days before they take effect. Continued use of the service after
          the effective date constitutes acceptance of the updated Terms.
        </p>
      </LegalSection>

      <LegalSection title="17. Contact">
        <p>
          Newground AI · United States<br />
          Email:{" "}
          <a href="mailto:james@newgroundai.com" className="text-[#1D6AFF] hover:underline">james@newgroundai.com</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
